/**
 * Resource Conflict Detection
 *
 * Client-side warnings for resource scheduling conflicts.
 *
 * DDD Note: This is a CLIENT-SIDE VALIDATION for UX purposes.
 * The backend/domain layer is the source of truth for enforcement.
 * We show warnings to help users avoid conflicts before submission.
 */

import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';
import { isSameDay } from 'date-fns';

export interface ConflictCheckResult {
  hasConflict: boolean;
  conflictingJobs: JobCardViewModel[];
  message?: string;
}

/**
 * Check if a driver is already assigned to another job on the same date
 *
 * @param jobs - All jobs to check against
 * @param jobId - ID of job being moved/scheduled (exclude from check)
 * @param newDate - New date being proposed
 * @param driverId - Driver ID to check
 * @returns Conflict check result
 */
export function checkDriverConflict(
  jobs: JobCardViewModel[],
  jobId: string,
  newDate: Date,
  driverId: string | undefined
): ConflictCheckResult {
  if (!driverId) {
    return { hasConflict: false, conflictingJobs: [] };
  }

  const conflictingJobs = jobs.filter(job => {
    // Don't check against self
    if (job.id === jobId) return false;

    // Check if same driver
    if (job.assignedDriver?.id !== driverId) return false;

    // Check if same day
    const jobDate = new Date(job.startDateTimestamp);
    return isSameDay(jobDate, newDate);
  });

  if (conflictingJobs.length > 0) {
    const jobNames = conflictingJobs.map(j => j.displayName).join(', ');
    return {
      hasConflict: true,
      conflictingJobs,
      message: `Driver is already assigned to: ${jobNames}`,
    };
  }

  return { hasConflict: false, conflictingJobs: [] };
}

/**
 * Check if a vehicle is already assigned to another job on the same date
 *
 * @param jobs - All jobs to check against
 * @param jobId - ID of job being moved/scheduled (exclude from check)
 * @param newDate - New date being proposed
 * @param vehicleId - Vehicle ID to check
 * @returns Conflict check result
 */
export function checkVehicleConflict(
  jobs: JobCardViewModel[],
  jobId: string,
  newDate: Date,
  vehicleId: string | undefined
): ConflictCheckResult {
  if (!vehicleId) {
    return { hasConflict: false, conflictingJobs: [] };
  }

  const conflictingJobs = jobs.filter(job => {
    if (job.id === jobId) return false;
    if (job.assignedVehicle?.id !== vehicleId) return false;

    const jobDate = new Date(job.startDateTimestamp);
    return isSameDay(jobDate, newDate);
  });

  if (conflictingJobs.length > 0) {
    const jobNames = conflictingJobs.map(j => j.displayName).join(', ');
    return {
      hasConflict: true,
      conflictingJobs,
      message: `Vehicle is already assigned to: ${jobNames}`,
    };
  }

  return { hasConflict: false, conflictingJobs: [] };
}

/**
 * Check if a container is already assigned to another active job
 *
 * Note: Containers can only be in one place at a time.
 * This checks for any active job (not completed/cancelled).
 *
 * @param jobs - All jobs to check against
 * @param jobId - ID of job being moved/scheduled (exclude from check)
 * @param containerId - Container ID to check
 * @returns Conflict check result
 */
export function checkContainerConflict(
  jobs: JobCardViewModel[],
  jobId: string,
  containerId: string | undefined
): ConflictCheckResult {
  if (!containerId) {
    return { hasConflict: false, conflictingJobs: [] };
  }

  const conflictingJobs = jobs.filter(job => {
    if (job.id === jobId) return false;
    if (job.assignedContainer?.id !== containerId) return false;

    // Container conflicts apply to any non-completed/cancelled job
    return job.status !== 'completed' && job.status !== 'cancelled';
  });

  if (conflictingJobs.length > 0) {
    const jobNames = conflictingJobs.map(j => j.displayName).join(', ');
    return {
      hasConflict: true,
      conflictingJobs,
      message: `Container is already assigned to active job: ${jobNames}`,
    };
  }

  return { hasConflict: false, conflictingJobs: [] };
}

/**
 * Check all resource conflicts for a job being rescheduled
 *
 * Convenience function that checks driver, vehicle, and container.
 *
 * @param jobs - All jobs to check against
 * @param job - Job being moved/rescheduled
 * @param newDate - New date being proposed
 * @returns Array of all conflicts found
 */
export function checkAllResourceConflicts(
  jobs: JobCardViewModel[],
  job: JobCardViewModel,
  newDate: Date
): ConflictCheckResult[] {
  const conflicts: ConflictCheckResult[] = [];

  const driverConflict = checkDriverConflict(
    jobs,
    job.id,
    newDate,
    job.assignedDriver?.id
  );
  if (driverConflict.hasConflict) {
    conflicts.push(driverConflict);
  }

  const vehicleConflict = checkVehicleConflict(
    jobs,
    job.id,
    newDate,
    job.assignedVehicle?.id
  );
  if (vehicleConflict.hasConflict) {
    conflicts.push(vehicleConflict);
  }

  const containerConflict = checkContainerConflict(
    jobs,
    job.id,
    job.assignedContainer?.id
  );
  if (containerConflict.hasConflict) {
    conflicts.push(containerConflict);
  }

  return conflicts;
}
